import { eye, needle, shield, sprite, target } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * อาวุธลับพันดารา (พเนจร, yin): the wanderer's thrown iron. Darts with pale fletching, flying knives,
 * needles, sail-tacks and smoke, in cold steel against the night tile; flights are star-white. No poison
 * green and no blood: a hit reads as a bright star point, not a wound.
 */

const STEEL = C.steel;
const STEEL_DK = '#8a94b0';
const FLETCH = '#e8e0ff';
const STARLIGHT = '#f4eeff';
const SMOKE = '#9a8cb8';
const SMOKE_HI = '#d4ccea';

/** A dart flying right (10×5): fletching, shaft, iron head. */
function dart(p: Pix, x: number, y: number, flip = false): void {
  sprite(p, x, y, ['ff........', '.ff.......', '..sssssshh', '.ff.......', 'ff........'], { f: FLETCH, s: STEEL_DK, h: STEEL }, { flip });
}

/** A flying knife along (x0,y0)->(x1,y1): ring pommel, short grip, leaf blade. */
function knife(p: Pix, x0: number, y0: number, x1: number, y1: number): void {
  const mx = x0 + (x1 - x0) * 0.35;
  const my = y0 + (y1 - y0) * 0.35;
  p.ring(x0, y0, 1.5, 1.5, STEEL_DK, 0, Math.PI * 2);
  p.line(x0, y0, mx, my, STEEL_DK);
  p.thick(mx, my, x1, y1, STEEL, 2);
  p.px(x1, y1, C.white);
}

/** A sail-tack caltrop: four iron points (5×5). */
function tack(p: Pix, x: number, y: number): void {
  sprite(p, x, y, ['s...s', '.sss.', '.shs.', '.sss.', 's...s'], { s: STEEL_DK, h: STEEL });
}

registerIcons('skill', {
  // Two darts in flight.
  'hw-dart': (p) => {
    dart(p, 4, 7);
    dart(p, 14, 18);
    for (const [y, x] of [
      [9, 1],
      [20, 9],
    ] as const)
      p.rect(x, y, 3, 1, SMOKE);
  },
  // A net of needles thrown over a guard.
  'hw-snare': (p) => {
    shield(p, 12, 10, 14, 15, STEEL_DK, STEEL);
    for (let k = 0; k < 4; k++) {
      p.line(3 + k * 6, 3, 9 + k * 6, 19, SMOKE_HI);
      p.line(21 - k * 6, 3, 15 - k * 6, 19, SMOKE_HI);
    }
    p.ring(14, 3, 11, 2, SMOKE_HI, 0, Math.PI);
    for (const [x, y] of [
      [3, 3],
      [26, 3],
      [3, 19],
    ] as const)
      p.rect(x - 1, y - 1, 3, 3, STEEL);
  },
  // Three flying knives fanned out.
  'hw-knives': (p) => {
    knife(p, 4, 10, 27, 4);
    knife(p, 3, 18, 28, 16);
    knife(p, 4, 26, 27, 28);
  },
  // A rain of needles slanting down across the tile.
  'hw-rain': (p) => {
    for (let k = 0; k < 9; k++) {
      const x = 3 + ((k * 7) % 24);
      const y = 3 + ((k * 11) % 16);
      needle(p, x, y, x + 4, y + 7, STEEL_DK, C.white);
    }
    p.rect(2, 28, 28, 1, SMOKE);
  },
  // Sail-tacks scattered under the feet.
  'hw-caltrops': (p) => {
    p.ell(16, 23, 13, 4, '#3a3050');
    tack(p, 5, 19);
    tack(p, 13, 22);
    tack(p, 21, 19);
    tack(p, 17, 15);
    sprite(p, 9, 4, ['.bb.', 'bbbb', 'bbbb', 'bbbb', '.bb.', 'bbb.', 'bbbb'], { b: SMOKE_HI }, { k: 1 });
    p.rect(9, 11, 5, 1, STEEL_DK);
  },
  // A smoke bomb bursts and the figure is gone into it.
  'hw-smoke': (p) => {
    for (const [x, y, r] of [
      [10, 20, 6],
      [20, 19, 7],
      [15, 13, 6],
      [24, 11, 4],
      [7, 12, 3.5],
    ] as const) {
      p.ell(x, y, r, r, SMOKE);
      p.ell(x - 1, y - 1, r - 2, r - 2, SMOKE_HI);
    }
    sprite(p, 14, 8, ['.hh.', '.hh.', 'bbbb', 'b..b'], { h: '#6a5a8a', b: '#6a5a8a' });
    p.ell(9, 28, 2, 1, STEEL_DK);
  },
  // Breath held on the mark: an eye over a crosshair.
  'hw-aim': (p) => {
    target(p, 17, 19, 8, STARLIGHT);
    p.rect(17, 9, 1, 4, STARLIGHT).rect(17, 26, 1, 4, STARLIGHT).rect(7, 19, 4, 1, STARLIGHT).rect(24, 19, 4, 1, STARLIGHT);
    eye(p, 17, 19, 6, '#6a3aa8');
  },
  // Ten thousand stars: every piece thrown at once, a ring of flights round a bright core.
  'hw-stars': (p) => {
    for (let k = 0; k < 12; k++) {
      const a = (k * Math.PI) / 6;
      const r0 = 5;
      const r1 = 12;
      needle(p, 16 + Math.cos(a) * r0, 16 + Math.sin(a) * r0, 16 + Math.cos(a) * r1, 16 + Math.sin(a) * r1, k % 3 ? STEEL_DK : FLETCH, C.white);
    }
    p.ell(16, 16, 3, 3, STARLIGHT);
    p.px(16, 16, C.white);
  },
});
